import React, { useState, useEffect } from 'react';
import { superadminApi } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { RefreshCw, Play, Loader2, CheckCircle2, XCircle, Clock, CalendarClock, Activity } from 'lucide-react';
import { format } from 'date-fns';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return format(new Date(iso), 'd MMM yyyy, HH:mm'); } catch { return iso; }
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return format(new Date(iso), 'd MMM yyyy'); } catch { return iso; }
};

const StatusPill = ({ status }) => {
  const map = {
    success: 'text-emerald-600 bg-emerald-500/10',
    noop: 'text-muted-foreground bg-secondary',
    error: 'text-red-600 bg-red-500/10',
  };
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${map[status] || 'bg-secondary'}`}>{status}</span>;
};

const RolloverMonitor = () => {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        superadminApi.getRolloverStatus(),
        superadminApi.getRolloverRuns(100),
      ]);
      setStatus(s.data);
      setRuns(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load rollover status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    if (!window.confirm('Run the due-rollover check now? Only tenants whose leave year starts today are affected. This is idempotent and safe.')) return;
    setRunning(true);
    try {
      const r = await superadminApi.runRolloverNow();
      const d = r.data;
      toast.success(`Checked ${d.tenants_due} due tenant(s) — ${d.rows_created} allowance rows created${d.errors ? `, ${d.errors} error(s)` : ''}.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run rollover');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-red-500" /></div>;
  }

  const sched = status?.scheduler || {};
  const tenants = status?.tenants || [];
  const dueToday = tenants.filter((t) => t.due_today);

  return (
    <div className="space-y-6">
      {/* Scheduler health */}
      <div className="card-hive p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sched.running ? 'bg-emerald-500/15 text-emerald-600' : 'bg-red-500/15 text-red-600'}`}>
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                Rollover scheduler
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${sched.running ? 'text-emerald-600 bg-emerald-500/10' : 'text-red-600 bg-red-500/10'}`}>
                  {sched.running ? 'Running' : 'Not running'}
                </span>
              </h3>
              <p className="text-sm text-muted-foreground">Runs daily at {sched.run_time_local || '00:05'} (server time). Rolls each tenant into their new leave year on the 1st of their start month.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
            <Button className="btn-primary" onClick={runNow} disabled={running}>
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />} Run due rollovers now
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
          <div className="rounded-lg bg-secondary/40 p-3">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Started</div>
            <div className="text-sm font-medium mt-1">{fmt(sched.started_at)}</div>
          </div>
          <div className="rounded-lg bg-secondary/40 p-3">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Last check</div>
            <div className="text-sm font-medium mt-1">{fmt(sched.last_check_at)}</div>
          </div>
          <div className="rounded-lg bg-secondary/40 p-3">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Next check</div>
            <div className="text-sm font-medium mt-1">{fmt(sched.next_check_at)}</div>
          </div>
          <div className="rounded-lg bg-secondary/40 p-3">
            <div className="text-xs text-muted-foreground">Server date</div>
            <div className="text-sm font-medium mt-1">{fmtDate(status?.server_date)}</div>
          </div>
        </div>

        {dueToday.length > 0 && (
          <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            <span className="font-medium text-amber-700">{dueToday.length} tenant(s) due today:</span>{' '}
            {dueToday.map((t) => t.company_name).join(', ')}
          </div>
        )}
      </div>

      {/* Upcoming schedule per tenant */}
      <div className="card-hive p-6">
        <h3 className="font-heading font-bold text-lg mb-1">Per-tenant schedule</h3>
        <p className="text-sm text-muted-foreground mb-4">When each company's leave year starts, and the last rollover we ran for them.</p>
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 sticky top-0">
                <tr className="text-left">
                  <th className="p-2.5 font-medium">Company</th>
                  <th className="p-2.5 font-medium">Leave year starts</th>
                  <th className="p-2.5 font-medium">Next due</th>
                  <th className="p-2.5 font-medium">Last run</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.tenant_id} className="border-t border-border/50">
                    <td className="p-2.5">{t.company_name}{t.due_today && <span className="ml-2 text-[11px] text-amber-700 bg-amber-500/10 px-1.5 py-0.5 rounded-full">due today</span>}</td>
                    <td className="p-2.5 text-muted-foreground">{t.leave_year_start_label}</td>
                    <td className="p-2.5">{fmtDate(t.next_due_date)}</td>
                    <td className="p-2.5">
                      {t.last_run
                        ? <span className="flex items-center gap-2"><StatusPill status={t.last_run.status} /><span className="text-muted-foreground text-xs">{t.last_run.year} · +{t.last_run.created} · {fmtDate(t.last_run.at)}</span></span>
                        : <span className="text-muted-foreground text-xs">never</span>}
                    </td>
                  </tr>
                ))}
                {tenants.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">No tenants.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Run history log */}
      <div className="card-hive p-6">
        <h3 className="font-heading font-bold text-lg mb-1">Run history</h3>
        <p className="text-sm text-muted-foreground mb-4">Every scheduled, catch-up, and manual rollover, newest first.</p>
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 sticky top-0">
                <tr className="text-left">
                  <th className="p-2.5 font-medium">When</th>
                  <th className="p-2.5 font-medium">Company</th>
                  <th className="p-2.5 font-medium">Year</th>
                  <th className="p-2.5 font-medium">Trigger</th>
                  <th className="p-2.5 font-medium">Status</th>
                  <th className="p-2.5 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t border-border/50 align-top">
                    <td className="p-2.5 whitespace-nowrap text-muted-foreground">{fmt(r.started_at)}</td>
                    <td className="p-2.5">{r.tenant_name || '—'}</td>
                    <td className="p-2.5">{r.year}</td>
                    <td className="p-2.5"><span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary">{r.trigger}</span></td>
                    <td className="p-2.5">
                      {r.status === 'error'
                        ? <span className="flex items-center gap-1 text-red-600"><XCircle className="w-3.5 h-3.5" /><StatusPill status={r.status} /></span>
                        : <StatusPill status={r.status} />}
                    </td>
                    <td className="p-2.5 text-muted-foreground text-xs">{r.message}</td>
                  </tr>
                ))}
                {runs.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No runs logged yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolloverMonitor;
